import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import config from "../../config/config";
import { apiCall } from "../../_services/apiCall";
import { sweetAlert } from "../../utils/UniversalFunction";
import { INITIAL_ACCEPT_SUPPLIER_FORM } from "./acceptSupplierInvitationConstants";
import {
  fetchSupplierIndustries,
  getSubIndustriesForIndustry,
} from "../Company Sub Admin/Pages/supplier/hooks/supplierIndustries";
import {
  buildAcceptPayload,
  formatApiErrorMessage,
  parseInvitationResponse,
} from "./acceptSupplierInvitationHelpers";
import {
  validateAcceptForm,
  validateContactForm,
} from "./acceptSupplierInvitationValidation";

/**
 * Owns the entire accept-invitation page: token load, invitation fetch,
 * form state, industry dropdown, validation, submit. The page component
 * only renders the form; every state transition and side effect lives here.
 */
export default function useAcceptSupplierInvitation() {
  const { token } = useParams();
  const history = useHistory();

  const [form, setForm] = useState(INITIAL_ACCEPT_SUPPLIER_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [inviteValid, setInviteValid] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [industriesLoading, setIndustriesLoading] = useState(false);
  const [selectedIndustryId, setSelectedIndustryId] = useState("");
  const [selectedSubIndustryId, setSelectedSubIndustryId] = useState("");
  const [canUpdate, setCanUpdate] = useState(true);
  const [contactOnlyUpdate, setContactOnlyUpdate] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [supplierId, setSupplierId] = useState(null);
  const [invitationMeta, setInvitationMeta] = useState(null);

  const canSubmit = canUpdate || contactOnlyUpdate;
  const contactOnlyMode = contactOnlyUpdate && !canUpdate;
  const contactEditable = canUpdate || contactOnlyUpdate;
  const profileReadOnly = !canUpdate;

  const loadInvitationDetails = useCallback(async () => {
    setLoadingInvite(true);
    setLoadError("");
    setInviteValid(false);
    const { isSuccess, data, message } = await apiCall(
      `${config.PRELOGIN_API_URL_COMPANY}suppliers/invitation/${encodeURIComponent(token)}`,
      {},
      {},
      "GET",
      false,
      false
    );
    setLoadingInvite(false);
    if (isSuccess) {
      const inv = data?.data ?? data ?? {};
      const parsed = parseInvitationResponse(inv);
      setCanUpdate(parsed.canUpdate);
      setContactOnlyUpdate(parsed.contactOnlyUpdate);
      setCompanyId(parsed.companyId);
      setSupplierId(parsed.supplierId);
      setForm(parsed.form);
      setSelectedIndustryId(parsed.industryId);
      setSelectedSubIndustryId(parsed.subIndustryId);
      setInvitationMeta({
        statusKey: inv?.status_key ?? "",
        companyName:
          inv?.invited_company_name ?? parsed.form?.company_name ?? "",
        expiresAt: inv?.expires_at ?? null,
        acceptedAt: inv?.accepted_at ?? null,
      });
      setInviteValid(true);
      return;
    }
    setLoadError(
      message ||
        data?.message ||
        data?.error ||
        "This invitation link is invalid or has expired."
    );
  }, [token]);

  useEffect(() => {
    if (!token) {
      setLoadError("Invalid invitation link.");
      setInviteValid(false);
      setLoadingInvite(false);
      return;
    }
    loadInvitationDetails();
  }, [token, loadInvitationDetails]);

  useEffect(() => {
    if (!inviteValid) return undefined;
    let cancelled = false;
    (async () => {
      setIndustriesLoading(true);
      const list = await fetchSupplierIndustries();
      if (!cancelled) {
        setIndustries(list);
        setIndustriesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteValid]);

  const subIndustries = useMemo(
    () => getSubIndustriesForIndustry(industries, selectedIndustryId),
    [industries, selectedIndustryId]
  );

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleIndustryChange = useCallback((industryId) => {
    setSelectedIndustryId(industryId);
    setSelectedSubIndustryId("");
  }, []);

  const handleSubIndustryChange = useCallback((subIndustryId) => {
    setSelectedSubIndustryId(subIndustryId);
  }, []);

  const validationErrors = useMemo(() => {
    if (!canSubmit) return {};
    if (contactOnlyMode) {
      return validateContactForm(form, { requirePassword: false });
    }
    if (canUpdate) {
      return validateAcceptForm(form, selectedIndustryId, selectedSubIndustryId, {
        canUpdate: true,
      });
    }
    return {};
  }, [
    form,
    selectedIndustryId,
    selectedSubIndustryId,
    canUpdate,
    contactOnlyMode,
    canSubmit,
  ]);

  const submit = useCallback(
    async (e) => {
      e?.preventDefault();
      if (!canSubmit) return;

      setSubmitted(true);
      setSubmitError("");

      if (!token) {
        setSubmitError("Invalid invitation link.");
        return;
      }

      const submitValidationErrors = contactOnlyMode
        ? validateContactForm(form, { requirePassword: false })
        : canUpdate
          ? validateAcceptForm(
              form,
              selectedIndustryId,
              selectedSubIndustryId,
              { canUpdate: true }
            )
          : {};

      if (Object.keys(submitValidationErrors).length > 0) {
        const firstMessage =
          Object.values(submitValidationErrors).find(Boolean) ||
          "Please fix the highlighted fields before submitting.";
        sweetAlert("error", firstMessage, false);
        document
          .querySelector(".accept-supplier-invite .help-block")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      setSubmitting(true);
      const payload = buildAcceptPayload(
        form,
        selectedIndustryId,
        selectedSubIndustryId
      );

      const result = await apiCall(
        `${config.PRELOGIN_API_URL_COMPANY}suppliers/invitation/${encodeURIComponent(token)}`,
        { "Content-Type": "application/json" },
        payload,
        "PUT",
        true,
        false
      );
      setSubmitting(false);

      if (!result) {
        setSubmitError("Request failed. Please try again.");
        return;
      }

      if (result.isSuccess) {
        sweetAlert(
          "success",
          result.message ||
            (contactOnlyMode
              ? "Contact details saved successfully."
              : "Invitation accepted successfully."),
          true
        );
        setTimeout(() => history.push("/Login"), 1500);
        return;
      }

      const errMsg = formatApiErrorMessage(result);
      setSubmitError(errMsg);
      sweetAlert("error", errMsg, false);
    },
    [
      token,
      form,
      canSubmit,
      canUpdate,
      contactOnlyMode,
      selectedIndustryId,
      selectedSubIndustryId,
      history,
    ]
  );

  return {
    form,
    setField,
    industries,
    industriesLoading,
    subIndustries,
    selectedIndustryId,
    selectedSubIndustryId,
    handleIndustryChange,
    handleSubIndustryChange,
    submitted,
    validationErrors,
    submit,
    submitting,
    submitError,
    loadingInvite,
    loadError,
    inviteValid,
    canSubmit,
    canUpdate,
    contactOnlyMode,
    contactEditable,
    profileReadOnly,
    invitationMeta,
    companyId,
    supplierId,
    goToLogin: () => history.push("/Login"),
  };
}
